import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal
} from "shared/components";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import {
  ApplyBrandingFailure,
  ApplyBrandingResponse
} from "types/landlord/address";
import { propertiesLabel, type PropertyDictionary } from "./helpers";

const REASON_LABELS: Record<string, string> = {
  not_found: "Not found or not eligible for bulk branding",
  validation_failed: "Validation error"
};

const FIELD_LABELS: Record<string, string> = {
  landlord_name: "Landlord name",
  landlord_phone: "Landlord phone",
  landlord_email: "Landlord email"
};

const getReasonText = (failure: ApplyBrandingFailure) =>
  failure.message || REASON_LABELS[failure.reason] || failure.reason;

const getFieldLabels = (fields: string[]) =>
  fields.map((field) => FIELD_LABELS[field] || field).join(", ");

const getTitle = ({
  jobFailedOutright,
  hasFailures
}: {
  jobFailedOutright: boolean;
  hasFailures: boolean;
}) => {
  if (jobFailedOutright) return "Could not apply branding";
  if (hasFailures) return "Branding applied with some errors";

  return "Branding applied";
};

const getDescription = ({
  result,
  jobFailedOutright,
  hasFailures,
  dictionary
}: {
  result: ApplyBrandingResponse;
  jobFailedOutright: boolean;
  hasFailures: boolean;
  dictionary: PropertyDictionary;
}) => {
  if (jobFailedOutright)
    return (
      result.error ||
      "Something went wrong while applying branding. Please try again."
    );

  const applied = `Branding was applied to ${result.updated_count} ${propertiesLabel(result.updated_count, dictionary)}.`;

  if (hasFailures)
    return `${applied} ${result.failed.length} ${propertiesLabel(result.failed.length, dictionary)} could not be updated.`;

  return `${applied} Some fields were kept as-is because they are synced from the PMS.`;
};

const labelFor = (
  record: { property_id: number; property_name: string | null },
  dictionary: PropertyDictionary
) => record.property_name || `${dictionary.Property} #${record.property_id}`;

export const ApplyBrandingResultDialog = ({
  result,
  onClose
}: {
  result: ApplyBrandingResponse | null;
  onClose: () => void;
}) => {
  const dictionary = useUnitsPropertiesDictionary();

  if (!result) return null;

  const jobFailedOutright = result.status === "failed";
  const hasFailures = result.failed.length > 0;

  return (
    <Modal onClose={onClose} opened panelClassName="max-w-[520px] w-full">
      <header className="border-b border-alpha-40 px-6 py-6">
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-xl font-bold text-content-1000">
            {getTitle({ jobFailedOutright, hasFailures })}
          </h3>
          <IconButton
            HugeIcon={Cancel01Icon}
            onClick={onClose}
            size={IconButtonSize.BASE}
            variant={IconButtonVariant.TEXT_BASE}
          />
        </div>
        <p className="mt-2 text-sm text-content-400">
          {getDescription({
            result,
            jobFailedOutright,
            hasFailures,
            dictionary
          })}
        </p>
      </header>

      {hasFailures || result.skipped_fields.length > 0 ? (
        <div className="max-h-[280px] space-y-2 overflow-y-auto border-b border-alpha-40 px-6 py-4">
          {result.failed.map((failure) => (
            <div key={`failed-${failure.property_id}`}>
              <p className="truncate text-sm font-medium text-content-700">
                {labelFor(failure, dictionary)}
              </p>
              <p className="text-xs text-danger-500">
                {getReasonText(failure)}
              </p>
            </div>
          ))}

          {result.skipped_fields.map((skipped) => (
            <div key={`skipped-${skipped.property_id}`}>
              <p className="truncate text-sm font-medium text-content-700">
                {labelFor(skipped, dictionary)}
              </p>
              <p className="text-xs text-warning-700">
                Kept from PMS: {getFieldLabels(skipped.fields)}
              </p>
            </div>
          ))}
        </div>
      ) : null}

      <footer className="flex items-center justify-end px-6 py-4">
        <Button onClick={onClose} variant={ButtonVariant.PRIMARY}>
          Done
        </Button>
      </footer>
    </Modal>
  );
};
